import React, {
  useEffect,
  useState,
  useCallback,
  memo,
  useMemo
} from "react";

import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";

import "./Portfolio.css";

const formatAlbumName = (name) =>
  name
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const AlbumCard = memo(({ album, index, onOpen }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className="album-card fade-in"
      style={{
        animationDelay: `${index * 100}ms`
      }}
      onClick={() => onOpen(album.originalName)}
    >
      <div className="album-cover-container">
        {!loaded && (
          <div className="image-shimmer"></div>
        )}

        <img
          src={album.cover}
          alt={album.name}
          className={`album-cover ${loaded ? "loaded" : ""}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
        />

        <div className="album-overlay">
          <div className="album-name-overlay">
            {album.name}
          </div>

          <div className="album-meta">
            <div className="album-count">
              {album.count} Photos
            </div>

            <button
              className="view-album-btn"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(album.originalName);
              }}
            >
              View Album →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const Portfolio = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAlbums = useCallback(async () => {
    try {
      setLoading(true);
      
      // createdAt එක අනුව මුලින් දාපු ඒවා උඩටත්, අලුතින් දාන ඒවා යටටත් (ascending) Query එක සකස් කිරීම
      const q = query(collection(db, "collection_albums"), orderBy("createdAt", "asc"));
      const querySnapshot = await getDocs(q);
      
      const albumData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const images = data.images || [];

        if (images.length > 0) {
          albumData.push({
            id: doc.id,
            name: formatAlbumName(data.albumName || doc.id),
            originalName: data.albumName || doc.id,
            cover: images[0],
            count: images.length,
            createdAt: data.createdAt
          });
        }
      });

      setAlbums(albumData);
    } catch (error) {
      console.error("Portfolio Error:", error);
      
      try {
        const fallbackSnapshot = await getDocs(collection(db, "collection_albums"));
        const fallbackData = [];
        fallbackSnapshot.forEach((doc) => {
          const data = doc.data();
          const images = data.images || [];
          if (images.length > 0) {
            fallbackData.push({
              id: doc.id,
              name: formatAlbumName(data.albumName || doc.id),
              originalName: data.albumName || doc.id,
              cover: images[0],
              count: images.length
            });
          }
        });
        setAlbums(fallbackData);
      } catch (fallbackError) {
        console.error("Fallback Error:", fallbackError);
        setAlbums([]);
      }

    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const openAlbum = useCallback(
    (name) => {
      navigate(`/album/${name}`);
    },
    [navigate]
  );

  const totalPhotos = useMemo(() => {
    return albums.reduce(
      (sum, item) => sum + item.count,
      0
    );
  }, [albums]);

  return (
    <div className="albums-container">
      <div className="albums-header">
        <h1 className="header-title">
          <span className="title-accent">
            Collection
          </span>
        </h1>

        <p className="header-subtitle">
          {loading
            ? "Loading portfolio..."
            : `A curated selection of ${albums.length} albums containing ${totalPhotos} moments in time`}
        </p>
      </div>

      <div className="albums-grid">
        {loading
          ? Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="album-card">
                <div className="skeleton-card">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-line title"></div>
                    <div className="skeleton-line small"></div>
                  </div>
                </div>
              </div>
            ))
          : albums.map((album, index) => (
              <AlbumCard
                key={album.id}
                album={album}
                index={index}
                onOpen={openAlbum}
              />
            ))}
      </div>

      {!loading && albums.length > 0 && (
        <div className="page-footer">
          <p className="footer-text">
            <strong>{albums.length}</strong> albums • <strong>{totalPhotos}</strong> photographs
          </p>
        </div>
      )}
    </div>
  );
};

export default Portfolio;